import React from "react";
import { motion } from "framer-motion";
import { Logo } from "../Logo";
import { Marquee } from "../Marquee";
import { brandLogos, brands, links } from "../../data/portfolio";

/**
 * Client wall. Real logo artwork sits in a static contact-sheet grid, and the full company
 * list runs beneath it as a single-line text carousel.
 */
export function BrandsSection() {
  return <section id="clients" aria-labelledby="clients-heading" data-cursor-theme="light" className="relative overflow-hidden bg-[#e9e9e6] py-16 text-[#0a0e0d] md:py-24" data-id="element-64" data-mp="7mdc1et70k2p">
      <div className="relative z-10 mx-auto flex max-w-[1480px] items-center justify-between gap-4 px-5 font-mono text-sm font-semibold uppercase tracking-[0.16em] md:px-8 md:text-base md:tracking-[0.18em]" data-id="element-65" data-mp="gmta28r62z06">
        <div className="flex items-center gap-3 md:gap-5" data-id="element-66" data-mp="3l19q6t16czhp">
          <span aria-hidden="true" data-id="element-67" data-mp="3l0rkl5smh8x4">002</span>
          <span aria-hidden="true" className="hidden h-px w-10 bg-[#0a0e0d]/35 sm:block md:w-14" data-id="element-68" data-mp="3p23917zrfo3q" />
          <h2 id="clients-heading" className="font-mono text-sm font-semibold uppercase tracking-[0.16em] md:text-base md:tracking-[0.18em]" data-id="element-69" data-mp="146wkstvszdq8">
            Brands I’ve Worked With
          </h2>
        </div>
        <span aria-hidden="true" className="flex shrink-0 items-center gap-2.5" data-id="element-70" data-mp="uftmu0y67ev6">
          <Logo tone="light" className="h-[18px] w-[40px]" data-id="element-71" data-mp="phs1tah6p5h2" />
          <span className="opacity-45" data-id="element-72" data-mp="ph8mm1ix4v6q">+</span>
        </span>
      </div>

      {/* Static logo grid, four across, sitting directly on the light band at full strength. */}
      <div className="relative z-10 mx-auto mt-10 max-w-[1480px] px-5 md:mt-14 md:px-8" data-id="element-73" data-mp="gfrb6wgghhod">
        <motion.ul initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-8%"
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-wrap overflow-hidden rounded-[20px] border-l border-t border-[#0a0e0d]/[0.14]" data-id="element-74" data-mp="bisf48d8f0nj">
          {brandLogos.map(brand => (/* Flex with grow, so a short final row stretches to fill instead of leaving empty cells. */
        <li key={brand.name} className="group grid min-h-[124px] min-w-0 grow basis-1/2 place-items-center border-b border-r border-[#0a0e0d]/[0.14] px-4 py-6 transition-colors duration-500 hover:bg-[#0a0e0d]/[0.04] md:min-h-[156px] md:basis-1/4 md:px-5 md:py-7 lg:min-h-[172px]" data-id="element-75" data-mp="3ygg0aj535k5">
              {/* Capped on both axes so wide and square marks read at the same optical weight.
                  The wrapper carries the hover lift so a per-logo optical scale can sit on the image. */}
              <span className="grid place-items-center transition-transform duration-500 group-hover:scale-105" data-id="element-76" data-mp="7za2r0kuiebg">
                <img src={brand.src} alt={brand.name} loading="lazy" draggable={false} style={brand.scale ? {
              transform: `scale(${brand.scale})`
            } : undefined} className={`max-h-[76px] w-auto max-w-full select-none object-contain md:max-h-[104px] lg:max-h-[118px] ${brand.keepColor ? "" : "[filter:brightness(0)]"}`} data-id="element-77" data-mp="cx1x018yqcit" />
              </span>
            </li>))}
        </motion.ul>
      </div>

      {/* Every company name, running as one continuous line. */}
      <div className="relative z-10 mx-auto mt-5 max-w-[1480px] px-5 md:px-8" data-id="element-78" data-mp="x10tb1358x9x">
        <div data-cursor-theme="dark" className="overflow-hidden rounded-[20px] bg-[#0a0e0d] py-7 md:py-8" data-id="element-79" data-mp="wywrcoot28ca">
          <div className="flex items-center justify-between gap-4 px-6 font-mono text-[11px] uppercase tracking-[0.18em] text-white/45 md:px-9" data-id="element-80" data-mp="s03aqne8217w">
            <span className="flex items-center gap-3" data-id="element-81" data-mp="111jkcsx7pzb2">
              <span aria-hidden="true" className="dot-blink h-1.5 w-1.5 rounded-full bg-[#54f6c6]" data-id="element-82" data-mp="dd0d8gpwfskk" />
              Client list
            </span>
          </div>

          {/* Slower than the tool ticker, so 20 client names stay readable as they pass. */}
          <Marquee items={brands} className="mt-6" durationSeconds={140} data-id="element-83" data-mp="m1yvm4byvlk0" />
        </div>
      </div>

      <div className="relative z-10 mx-auto mt-12 flex max-w-[1480px] flex-col gap-4 px-5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] md:mt-14 md:flex-row md:items-center md:justify-between md:px-8" data-id="element-84" data-mp="r0iiq82ar3dy">
        <p className="max-w-md text-[13px] normal-case tracking-[0.02em] text-[#0a0e0d]/70 sm:text-sm" data-id="element-85" data-mp="10rn0ffjcx2ch">
          Collaborated with 50+ companies across animation, post-production, and design.
        </p>
        <span className="flex flex-wrap items-center gap-x-5" data-id="element-86" data-mp="19sbega8r32pd">
          <a href={links.linkedin} target="_blank" rel="noreferrer" data-cursor="Open" className="inline-flex min-h-11 items-center transition-opacity hover:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0a0e0d]" data-id="element-87" data-mp="1eqmoom2yjf84">LinkedIn</a>
          <a href={links.whatsapp} target="_blank" rel="noreferrer" data-cursor="Open" className="inline-flex min-h-11 items-center transition-opacity hover:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0a0e0d]" data-id="element-88" data-mp="3lfj5i4e5hnsy">WhatsApp</a>
          <a href={links.email} data-cursor="Open" className="inline-flex min-h-11 items-center transition-opacity hover:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0a0e0d]" data-id="element-89" data-mp="393yth0jirp7n">Email</a>
          <span className="inline-flex min-h-11 items-center gap-2 text-[#0a0e0d]/60" data-id="element-90" data-mp="2z59fvjynoqx7"><span className="rec-pulse h-1.5 w-1.5 rounded-full bg-[#ff5f56]" data-id="element-91" data-mp="p86709zgs9o" />REC</span>
        </span>
      </div>

      <ul className="sr-only" data-id="element-92" data-mp="3rtygzizpow8y">
        {brands.map(brand => <li key={brand} data-id="element-93" data-mp="3mvwvkretckub">{brand}</li>)}
      </ul>
    </section>;
}